/** @jsxImportSource @emotion/react */

import { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { CSSObject } from '@emotion/react';
import { fonts } from '../../style/fonts';
import { colors } from '../../style/colors';
import { cardHeaderCss } from '../../style/shapes';
import { buttons } from '../../style/buttons';
import CloseIcon from '../../assets/close.svg';
import { LoginService } from '../../services/loginService';
import { submitContactForm } from '../../services/supportService';
import { useSelector } from 'react-redux';
import { getContactCategories, ContactCategoryValue } from './categories';
import { getUserRoleSelector } from '../../state-manager/selectors/appSelectors';
import { compressImage, CompressedImage } from './imageCompressor';
import { getUserSelector } from '../../models/factories/userFactories';

const TITLE_MAX = 120;
const DESCRIPTION_MAX = 4000;
const SUCCESS_AUTO_CLOSE_MS = 1500;

const modalCss: CSSObject = {
  paddingBottom: '20px',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '.contact-us-body': {
    width: '100%',
    flex: 1,
    overflow: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  '.contact-us-row': {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  // `&&` doubles the class so these beat `style/form.ts::formCss`, applied
  // globally from App.tsx AFTER component styles. It sets `white-space: nowrap`,
  // `overflow: hidden` and `text-overflow: ellipsis` on EVERY textarea, which
  // turned the Description into a single sideways-scrolling line. A single
  // class ties with `input, textarea, select` (0,1,0) and loses on source order.
  // The selector also reaches MUI's hidden shadow textarea, which must wrap
  // the same way or the autosize measures one line.
  '&& .contact-us-description textarea': {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    textOverflow: 'clip',
    overflowX: 'hidden',
    // Past maxRows MUI clears its inline `overflow: hidden`, so this is what
    // lets a long description scroll vertically instead of being clipped.
    overflowY: 'auto',
  },
  '.contact-us-footer': {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '12px 20px 0 20px',
    borderTop: `1px solid ${colors.dividerGrey}`,
  },
  '.contact-us-error': {
    color: colors.red,
    padding: '0 20px',
    minHeight: 18,
  },
  '.contact-us-success': {
    color: '#039855',
    padding: '0 20px',
    minHeight: 18,
  },
};

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; ticketKey: string }
  | { kind: 'error'; message: string };

export default function ContactUsModal({
  isOpen,
  setOpen,
  initialCategory,
}: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  initialCategory?: ContactCategoryValue;
}) {
  // The portal has two distinct identifiers for the logged-in user:
  // - `userId` from the Cognito `preferred_username` attribute, which is what
  //   the rest of the backend (and this Lambda's `claims.patientId` check)
  //   correlates against. May be a UUID for some accounts; not user-facing.
  // - `email` from the Cognito `email` attribute — the human-readable address
  //   shown in the management page. Used for display, and for both
  //   `contact_email` and `clinician_id` on the Jira ticket so triage has
  //   something to reply to.
  const userId = useMemo(
    () => LoginService.getInstance().getUserId() || '',
    [isOpen],
  );
  const email = useMemo(
    () => LoginService.getInstance().getEmail() || '',
    [isOpen],
  );

  // First name for the acknowledgement email's greeting. Comes from the
  // loaded user profile; may be absent (e.g. profile not loaded yet), in
  // which case the Lambda greets without a name.
  const user = useSelector(getUserSelector);
  const name = user?.firstName ?? '';

  // Patients get a reduced, plain-language category list; everyone else gets
  // the full list. The default selection must come from the role-appropriate
  // list (a patient has no 'BUG' option).
  const role = useSelector(getUserRoleSelector);
  const categories = useMemo(() => getContactCategories(role), [role]);
  const defaultCategory: ContactCategoryValue =
    initialCategory ?? categories[0]?.value ?? 'BUG';

  const [category, setCategory] =
    useState<ContactCategoryValue>(defaultCategory);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [allowContact, setAllowContact] = useState(true);
  const [attachment, setAttachment] = useState<CompressedImage | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset form whenever the modal is opened so a previous submission doesn't
  // leak in. Auto-close after success after a short delay.
  useEffect(() => {
    if (isOpen) {
      setCategory(defaultCategory);
      setTitle('');
      setDescription('');
      setAllowContact(true);
      setAttachment(null);
      setAttachmentError(null);
      setSubmitState({ kind: 'idle' });
    }
  }, [isOpen, initialCategory, defaultCategory]);

  useEffect(() => {
    if (submitState.kind !== 'success') {
      return;
    }
    const t = window.setTimeout(() => setOpen(false), SUCCESS_AUTO_CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [submitState, setOpen]);

  if (!isOpen) {
    return null;
  }

  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const titleValid =
    trimmedTitle.length > 0 && trimmedTitle.length <= TITLE_MAX;
  const descriptionValid =
    trimmedDescription.length > 0 &&
    trimmedDescription.length <= DESCRIPTION_MAX;
  const canSubmit =
    submitState.kind !== 'submitting' && titleValid && descriptionValid;

  const onCancel = (reason?: string) => {
    if (submitState.kind === 'submitting') {
      return;
    }
    if (reason === 'backdropClick') {
      return;
    }
    setOpen(false);
  };

  const onPickFile = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    setAttachmentError(null);
    try {
      const compressed = await compressImage(file);
      setAttachment(compressed);
    } catch (err) {
      setAttachment(null);
      const message =
        err instanceof Error
          ? err.message
          : 'Could not read the selected image.';
      setAttachmentError(message);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitState({ kind: 'submitting' });
    try {
      const result = await submitContactForm({
        category,
        title: trimmedTitle,
        description: trimmedDescription,
        allowContact,
        userId,
        email,
        name,
        attachment: attachment ?? undefined,
      });
      setSubmitState({ kind: 'success', ticketKey: result.ticketKey });
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Couldn’t send. Check your connection and try again.';
      setSubmitState({ kind: 'error', message });
    }
  };

  return (
    <Dialog
      css={{
        '.MuiDialog-paper': {
          width: 'min(90vw, 720px)',
          height: '100%',
        },
        '.MuiDialog-container': { height: '100%' },
      }}
      open={true}
      onClose={(_, reason) => onCancel(reason)}
    >
      <div css={modalCss}>
        <div css={cardHeaderCss}>
          Contact us
          <button type="button" disabled={submitState.kind === 'submitting'}>
            <CloseIcon width={13} onClick={() => onCancel()} />
          </button>
        </div>

        <div className="contact-us-body">
          <div css={{ ...fonts.text, color: colors.darkerGrey }}>
            Submitting as:{' '}
            <span css={{ ...fonts.largeLabel }}>{email || '(unknown)'}</span>
          </div>

          <div className="contact-us-row">
            <label css={{ ...fonts.largeLabel }} htmlFor="contact-category">
              What can we help with?
            </label>
            <Select
              id="contact-category"
              value={category}
              onChange={(e: SelectChangeEvent) =>
                setCategory(e.target.value as ContactCategoryValue)
              }
              size="small"
              fullWidth
              disabled={submitState.kind === 'submitting'}
            >
              {categories.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className="contact-us-row">
            <label css={{ ...fonts.largeLabel }} htmlFor="contact-title">
              Title
            </label>
            <TextField
              id="contact-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: TITLE_MAX }}
              size="small"
              fullWidth
              disabled={submitState.kind === 'submitting'}
              placeholder="Short summary"
            />
            <div css={{ ...fonts.mini, color: colors.darkerGrey }}>
              {trimmedTitle.length}/{TITLE_MAX}
            </div>
          </div>

          <div className="contact-us-row">
            <label css={{ ...fonts.largeLabel }} htmlFor="contact-description">
              Description
            </label>
            <TextField
              id="contact-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: DESCRIPTION_MAX }}
              className="contact-us-description"
              multiline
              minRows={6}
              maxRows={14}
              fullWidth
              disabled={submitState.kind === 'submitting'}
              placeholder="Steps to reproduce, what you expected, anything else useful"
            />
            <div css={{ ...fonts.mini, color: colors.darkerGrey }}>
              {trimmedDescription.length}/{DESCRIPTION_MAX}
            </div>
          </div>

          <div className="contact-us-row">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowContact}
                  onChange={(e) => setAllowContact(e.target.checked)}
                  disabled={submitState.kind === 'submitting'}
                />
              }
              label="It's OK to contact me about this issue"
            />
          </div>

          <div className="contact-us-row">
            <label css={{ ...fonts.largeLabel }}>
              Screenshot (optional, ≤ 5 MB)
            </label>
            {attachment ? (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  ...fonts.text,
                }}
              >
                <span>
                  {attachment.filename} (
                  {Math.round(attachment.blob.size / 1024)} KB)
                </span>
                <button
                  type="button"
                  css={{ ...buttons.secondarySmall, cursor: 'pointer' }}
                  onClick={() => setAttachment(null)}
                  disabled={submitState.kind === 'submitting'}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                {/* The browser's own file control looks out of place in the
                    portal, so it stays hidden and a portal-styled button
                    opens it (same secondarySmall style as Remove). */}
                <div css={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    type="button"
                    css={{ ...buttons.secondarySmall, cursor: 'pointer' }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={submitState.kind === 'submitting'}
                  >
                    Choose screenshot
                  </button>
                  <span css={{ ...fonts.text, color: colors.inactiveGrey }}>
                    No file chosen
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  aria-label="Screenshot file"
                  css={{ display: 'none' }}
                  onChange={(e) => onPickFile(e.target.files?.[0])}
                  disabled={submitState.kind === 'submitting'}
                />
                {attachmentError && (
                  <div
                    css={{
                      ...fonts.mini,
                      color: colors.red,
                      paddingTop: 4,
                    }}
                  >
                    {attachmentError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {submitState.kind === 'error' && (
          <div className="contact-us-error" css={fonts.text}>
            {submitState.message}
          </div>
        )}
        {submitState.kind === 'success' && (
          <div className="contact-us-success" css={fonts.text}>
            Thanks — we got it.
            {submitState.ticketKey ? ` Ticket: ${submitState.ticketKey}` : ''}
          </div>
        )}

        <div className="contact-us-footer">
          <button
            type="button"
            css={{ ...buttons.secondary, cursor: 'pointer' }}
            onClick={() => onCancel()}
            disabled={submitState.kind === 'submitting'}
          >
            Cancel
          </button>
          <button
            type="button"
            css={{
              ...buttons.primary,
              cursor: canSubmit ? 'pointer' : 'default',
            }}
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {submitState.kind === 'submitting' ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
